import * as THREE from "three";
import type { Design } from "../neon/Design";

const PADDING = 0.55;
const THICKNESS = 0.12;
const BACK_OFFSET = -0.18;
const MIN_SIZE = 1.2;

/**
 * A dark acrylic-style backplate that auto-fits the bounding box of all
 * tubes in the design. Sells the "this is a real sign mounted on a wall"
 * illusion — without it, tubes float.
 *
 * Updates lazily: subscribe to the Design and recompute on every change.
 * Hidden when the design is empty.
 */
export class Backplate {
  readonly mesh: THREE.Mesh;
  private mat: THREE.MeshStandardMaterial;

  constructor(private readonly design: Design) {
    this.mat = new THREE.MeshStandardMaterial({
      color: 0x0a0a10,
      metalness: 0.2,
      roughness: 0.6,
    });
    // BoxGeometry so we get a thin slab with a visible edge, not just a plane.
    this.mesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, THICKNESS), this.mat);
    this.mesh.name = "backplate";
    this.mesh.visible = false;
    this.mesh.renderOrder = -1;
    design.subscribe(() => this.refresh());
  }

  setVisible(visible: boolean): void {
    this.mesh.visible = visible && this.design.tubeCount > 0;
  }

  refresh(): void {
    if (this.design.tubeCount === 0) {
      this.mesh.visible = false;
      return;
    }
    const box = this.design.boundingBox();
    if (box.isEmpty()) {
      this.mesh.visible = false;
      return;
    }
    const size = new THREE.Vector3();
    box.getSize(size);
    const center = new THREE.Vector3();
    box.getCenter(center);

    const w = Math.max(size.x + PADDING * 2, MIN_SIZE);
    const h = Math.max(size.y + PADDING * 2, MIN_SIZE);

    this.mesh.geometry.dispose();
    this.mesh.geometry = new THREE.BoxGeometry(w, h, THICKNESS);
    this.mesh.position.set(center.x, center.y, BACK_OFFSET - THICKNESS * 0.5);
    this.mesh.visible = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.mat.dispose();
  }
}
